import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import type { SetStateAction } from 'react';

import { DependencyInfoOptions } from '../../../../../../../../shared/reports';
import { isFreeTier } from '../../../../../../../../shared/subscriptions';
import { window } from '../../../../../../../globals';
import { selectOptionFactory } from '../../../../../../../utils';
import { FilterPanel, FormLabelVariant } from '../../../../../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../../generic/Button';
import Flex from '../../../../../../generic/Flex';
import FloatingToolbar from '../../../../../../generic/FloatingToolbar';
import LabeledTextInput from '../../../../../../generic/Forms/LabeledTextInput';
import PaidFeatureGate from '../../../../../../generic/Gates/PaidFeatureGate';
import { SidebarLayout } from '../../../../../../layouts';
import { ReportCategories, ReportOptionDetails } from '../../../utils';
import { licenseIncludeOptions } from '../../constants';
import FormatSelect, { type FormatSelection } from '../FormatSelect';
import { mapFilterStateToReportConfig } from '../Options/mapFilterStateToReportConfig';
import { mapReportConfigToOptionState } from '../Options/mapReportConfigToFilterState';
import { SavedOptionsContainer } from '../Options/SavedOptionsContainer';
import type { PartialReportConfig } from '../../../../../../../../shared/reports/types';
import type { SelectOption, SetState } from '../../../../../../../types';
import type { DependencyInfoOption, ReportExclusionOptions } from '../../../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../../types';

import DependencyMetadataCheckboxGroup from './DependencyMetadataCheckboxGroup';
import { ExclusionGroup } from './exclusions/ExclusionGroup';
import IncludeCheckboxGroup from './IncludeCheckboxGroup';
import RegulatoryComplianceToggles from './RegulatoryComplianceToggles';
import ReportsV2Toggle from './ReportsV2Toggle';

import styles from './Filter.module.less';

const toSet = (opts: Array<{ value: string | number }>) =>
  new Set(opts.map((o) => o.value));

const setsEqual = <T extends string | number>(a: Set<T>, b: Set<T>) =>
  a.size === b.size && [...a].every(x => b.has(x));

type FilterState = {
  format: FormatName;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyInfoOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions: ReportExclusionOptions;
};

export type OnApply = (args: FilterState) => void;

type GenericProps = {
  appliedFormat: FormatName | null;
  format: FormatSelection;
  setFormat: SetState<FormatSelection>;
  formats: FormatSelection[];
  onApply: OnApply;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  setIsDirty: SetState<boolean>;
  isApplyButtonDisabled: boolean;
};

type SBOMProps = GenericProps & {
  category: 'sbom';
  authorName: string;
  authorEmail: string;
  supplier: string;
};
type OtherCategoryProps = GenericProps & {
  category: 'licensing' | 'vulnerability' | 'remediation-guidance';
};

type Props = SBOMProps | OtherCategoryProps;

const Filter = ({
  format,
  appliedFormat,
  setFormat,
  formats,
  onApply,
  setIsDirty,
  isApplyButtonDisabled,
  includeOptions: appliedIncludeOptions,
  dependencyMetadataOptions: appliedDependencyMetadataOptions,
  excludeOptions: appliedExcludeOptions,
  ...props
}: Props) => {
  const isPaidFeatureGateRestricted = isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const initialFlatIncludeValues = useMemo(
    () =>
      appliedIncludeOptions
        .filter((option) => !licenseIncludeOptions.has(option))
        .map((option) => ({ value: option, label: ReportOptionDetails[option]?.title ?? '' })),
    [appliedIncludeOptions],
  );
  const [flatIncludeValues, setFlatIncludeValues] =
    useState<Array<SelectOption<IncludeFilterOption>>>(initialFlatIncludeValues);

  // extract the package labels from the applied exclude options as SelectOptions for use in the UI
  // Note: more exclusions will need to be added here as they are added to
  // the ReportOptionExclusionDetails object in app/components/projects/generic/ReportGenerationV2/utils.ts
  // as well as the ExclusionGroup component
  // in app/components/projects/generic/ReportGenerationV2/shared/components/Filter/exclusions/ExclusionGroup.tsx
  // because each exclusion option may need to be handled differently in the UI
  const initialPackageLabelsExclusionValues = useMemo(
    () =>
      appliedExcludeOptions?.packageLabels
        ?.map((label) => selectOptionFactory(label))
        .filter((option) => option !== undefined),
    [appliedExcludeOptions],
  );
  const [packageLabelsExclusionValues, setPackageLabelsExclusionValues] = useState<SelectOption[]>(
    initialPackageLabelsExclusionValues ?? [],
  );

  // Used to track the base options in the currently selected saved option in order to determine if the user
  // has made any changes since saving.
  const [savedOptionFilterState, setSavedOptionFilterState] =
    useState<ReturnType<typeof mapReportConfigToOptionState> | null>(null);

  const initialIncludeCheckboxLicenseValues = useMemo(
    () =>
      appliedIncludeOptions
        .filter((option) => licenseIncludeOptions.has(option))
        .map((option) => ({ value: option, label: ReportOptionDetails[option]?.title ?? '' })),
    [appliedIncludeOptions],
  );
  const [includeCheckboxLicenseValues, setIncludeCheckboxLicenseValues] = useState<
    Array<SelectOption<IncludeFilterOption>>
  >(initialIncludeCheckboxLicenseValues);
  const initialDependencyMetadataCheckboxValues = useMemo(
    () =>
      appliedDependencyMetadataOptions.map((option) => ({
        value: option,
        label: DependencyInfoOptions[option],
      })),
    [appliedDependencyMetadataOptions],
  );
  const [dependencyMetadataCheckboxValues, setDependencyMetadataCheckboxValues] = useState<SelectOption[]>(
    initialDependencyMetadataCheckboxValues,
  );

  const propsAuthorName = props.category === ReportCategories.SBOM ? props.authorName : '';
  const propsAuthorEmail = props.category === ReportCategories.SBOM ? props.authorEmail : '';
  const propsSupplier = props.category === ReportCategories.SBOM ? props.supplier : '';

  // SBOM Author and Supplier state
  const [authorName, setAuthorName] = useState<string>(propsAuthorName);
  const [authorEmail, setAuthorEmail] = useState<string>(propsAuthorEmail);
  const [supplier, setSupplier] = useState<string>(propsSupplier);

  const handleAuthorNameChange = withSetIsDirty((value: SetStateAction<string>) => {
    setAuthorName(value);
  });

  const handleAuthorEmailChange = withSetIsDirty((value: SetStateAction<string>) => {
    setAuthorEmail(value);
  });

  const handleSupplierChange = withSetIsDirty((value: SetStateAction<string>) => {
    setSupplier(value);
  });

  function withSetIsDirty<T>(fn: (value: SetStateAction<T>) => void) {
    return (value: SetStateAction<T>) => {
      setIsDirty(true);
      fn(value);
    };
  }

  const includeOptions = _.uniq([
    ...(flatIncludeValues.map(({ value }) => value)),
    ...(includeCheckboxLicenseValues.map(({ value }) => value)),
  ]);

  const dependencyMetadataOptions = _.uniq(
    dependencyMetadataCheckboxValues.map(({ value }) => value as DependencyInfoOption),
  );

  const excludeOptions = {
    packageLabels: _.uniq([
      ...(packageLabelsExclusionValues.map(({ value }) => value)),
    ]),
  };

  const isSavedOptionDirty = useMemo(() => {
    if (!savedOptionFilterState) {
      return false;
    }
    return (
      !setsEqual(
        toSet(flatIncludeValues),
        toSet(savedOptionFilterState.flatIncludeValues),
      ) ||
      !setsEqual(
        toSet(includeCheckboxLicenseValues),
        toSet(savedOptionFilterState.licenseIncludeValues),
      ) ||
      !setsEqual(
        toSet(dependencyMetadataCheckboxValues),
        toSet(savedOptionFilterState.dependencyMetadataValues),
      ) ||
      !setsEqual(
        toSet(packageLabelsExclusionValues),
        toSet(savedOptionFilterState.packageLabelExclusionValues),
      )
    );
  }, [
    flatIncludeValues,
    includeCheckboxLicenseValues,
    dependencyMetadataCheckboxValues,
    packageLabelsExclusionValues,
    savedOptionFilterState,
  ]);

  const currentConfig = mapFilterStateToReportConfig(
    includeOptions,
    dependencyMetadataOptions,
    excludeOptions.packageLabels,
  );

  const handleSavedOptionApply = (config: PartialReportConfig | null) => {
    if (config === null) {
      setSavedOptionFilterState(null);
      return;
    }
    const mapped = mapReportConfigToOptionState(config);
    setSavedOptionFilterState(mapped);
    setFlatIncludeValues(mapped.flatIncludeValues);
    setIncludeCheckboxLicenseValues(mapped.licenseIncludeValues);
    setDependencyMetadataCheckboxValues(mapped.dependencyMetadataValues);
    setPackageLabelsExclusionValues(mapped.packageLabelExclusionValues);
    setIsDirty(true);
  };

  const onClickApply = () => {
    setIsDirty(false);
    onApply({
      format: format.value,
      includeOptions,
      dependencyMetadataOptions,
      authorName,
      authorEmail,
      supplier,
      excludeOptions });
  };

  const title = (
    <div className={styles.filterTitle}>
      <span>Options</span>
    </div>
  );

  function discardFormat() {
    if (appliedFormat) {
      setFormat({ label: appliedFormat, value: appliedFormat });
    } else if (formats[0]) {
      // This happens when user has not applied any format.
      // We need to reset it to the first format in the list.
      setFormat(formats[0]);
    }
  }

  function discardSBOMFields() {
    setAuthorName(propsAuthorName);
    setAuthorEmail(propsAuthorEmail);
    setSupplier(propsSupplier);
  }

  const isSBOMReport = props.category === ReportCategories.SBOM;

  return (
    <SidebarLayout.Sidebar>
      <FilterPanel title={title}>
        <FormatSelect format={format} formats={formats} setFormat={withSetIsDirty(setFormat)} />
        <ReportsV2Toggle />
        <SavedOptionsContainer
          currentConfig={currentConfig}
          handleSavedOptionApply={handleSavedOptionApply}
          isSavedOptionDirty={isSavedOptionDirty}
        />
        {isSBOMReport && (
          <>
            <LabeledTextInput
              labelVariant={FormLabelVariant.Filter}
              label="Author Name"
              placeholder="Enter author name"
              value={authorName}
              onChange={handleAuthorNameChange}
              size="small"
            />
            <LabeledTextInput
              labelVariant={FormLabelVariant.Filter}
              label="Author Email"
              placeholder="Enter author email"
              value={authorEmail}
              onChange={handleAuthorEmailChange}
              size="small"
            />
            <LabeledTextInput
              labelVariant={FormLabelVariant.Filter}
              label="Supplier"
              placeholder="Enter supplier name"
              value={supplier}
              onChange={handleSupplierChange}
              size="small"
            />
          </>
        )}
        <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
          <RegulatoryComplianceToggles
            setFlatIncludeValues={withSetIsDirty(setFlatIncludeValues)}
            setIncludeCheckboxLicenseValues={withSetIsDirty(setIncludeCheckboxLicenseValues)}
            setDependencyMetadataCheckboxValues={withSetIsDirty(setDependencyMetadataCheckboxValues)}
            flatIncludeValues={flatIncludeValues}
            includeCheckboxLicenseValues={includeCheckboxLicenseValues}
            dependencyMetadataCheckboxValues={dependencyMetadataCheckboxValues}
          />
        </PaidFeatureGate>
        <IncludeCheckboxGroup
          format={format}
          category={props.category}
          values={flatIncludeValues}
          setValues={withSetIsDirty(setFlatIncludeValues)}
          licenseValues={includeCheckboxLicenseValues}
          setLicenseValues={withSetIsDirty(setIncludeCheckboxLicenseValues)}
        />
        <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
          <ExclusionGroup
            format={format}
            category={props.category}
            values={packageLabelsExclusionValues}
            setValues={withSetIsDirty(setPackageLabelsExclusionValues)}
          />
        </PaidFeatureGate>
        <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
          <DependencyMetadataCheckboxGroup
            format={format}
            category={props.category}
            values={dependencyMetadataCheckboxValues}
            setValues={withSetIsDirty(setDependencyMetadataCheckboxValues)}
          />
        </PaidFeatureGate>
      </FilterPanel>
      <FloatingToolbar
        shouldShow={!isApplyButtonDisabled}
        text="Your changes have not been applied."
        actions={
          <Flex gap="8px" wrap="wrap">
            <Button onClick={onClickApply} type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.MEDIUM}>
              Apply
            </Button>
            <Button
              onClick={() => {
                setFlatIncludeValues(initialFlatIncludeValues);
                setIncludeCheckboxLicenseValues(initialIncludeCheckboxLicenseValues);
                setDependencyMetadataCheckboxValues(initialDependencyMetadataCheckboxValues);
                setPackageLabelsExclusionValues(initialPackageLabelsExclusionValues ?? []);
                discardFormat();
                discardSBOMFields();
                setIsDirty(false);
              }}
              type={BUTTON_TYPE.SLATE}
              size={BUTTON_SIZE.MEDIUM}
            >
              Discard
            </Button>
          </Flex>
        }
      />
    </SidebarLayout.Sidebar>
  );
};

export default Filter;
